/**
 * Tagada Rx — "how this works" map, shown at the top of onboarding.
 *
 * The merchant's mental model is usually "I sell a product". Rx is not
 * that: they own the brand and the traffic, Tagada owns everything
 * between the click and the package. This lays that split out before we
 * ask them for a KYC and a bank account, so both steps read as
 * consequences of the model rather than paperwork.
 *
 * No vendor names — merchants never see the clinical network or pharmacy
 * behind Tagada Rx.
 */

import {
  ArrowRight,
  ClipboardList,
  Megaphone,
  Package,
  Percent,
  Stethoscope,
  Truck,
  Wallet,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const PLATFORM_STEPS = [
  { icon: ClipboardList, key: 'medicalIntake' },
  { icon: Stethoscope, key: 'clinician' },
  { icon: Package, key: 'pharmacy' },
  { icon: Truck, key: 'fulfillment' },
] as const;

export function RxOnboardingDiagram() {
  const { t } = useTranslation('rx');
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('onboardingDiagram.title')}</CardTitle>
        <CardDescription>{t('onboardingDiagram.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid items-stretch gap-3 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,2.2fr)]">
          <div className="bg-muted/30 flex flex-col justify-center rounded-lg border p-4">
            <div className="flex items-center gap-2">
              <Megaphone className="text-primary h-4 w-4" />
              <span className="text-sm font-semibold">{t('onboardingDiagram.yourBrand.title')}</span>
            </div>
            <p className="text-muted-foreground mt-2 text-xs leading-relaxed">
              {t('onboardingDiagram.yourBrand.body')}
            </p>
          </div>

          <div className="text-muted-foreground flex items-center justify-center lg:px-1">
            <ArrowRight className="h-4 w-4 shrink-0 rotate-90 lg:rotate-0" />
          </div>

          <div className="border-primary/30 bg-primary/5 rounded-lg border p-4">
            <div className="flex items-center gap-2">
              <Stethoscope className="text-primary h-4 w-4" />
              <span className="text-sm font-semibold">{t('onboardingDiagram.tagadaRx.title')}</span>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {PLATFORM_STEPS.map((step) => {
                const Icon = step.icon;
                return (
                  <div key={step.key} className="bg-card rounded-md border px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <Icon className="text-muted-foreground h-3.5 w-3.5" />
                      <span className="text-xs font-semibold">
                        {t(`onboardingDiagram.steps.${step.key}.title`)}
                      </span>
                    </div>
                    <p className="text-muted-foreground mt-1 text-[11px] leading-relaxed">
                      {t(`onboardingDiagram.steps.${step.key}.body`)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex items-start gap-2 rounded-lg border px-3 py-2">
            <Percent className="text-primary mt-0.5 h-4 w-4 shrink-0" />
            <p className="text-muted-foreground text-xs leading-relaxed">
              <span className="text-foreground font-semibold">
                {t('onboardingDiagram.callout.take.strong')}
              </span>
              {t('onboardingDiagram.callout.take.body')}
            </p>
          </div>
          <div className="flex items-start gap-2 rounded-lg border px-3 py-2">
            <Wallet className="text-primary mt-0.5 h-4 w-4 shrink-0" />
            <p className="text-muted-foreground text-xs leading-relaxed">
              <span className="text-foreground font-semibold">
                {t('onboardingDiagram.callout.payout.strong')}
              </span>
              {t('onboardingDiagram.callout.payout.body')}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
